export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue }
export type JsonObject = Record<string, JsonValue>

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === '[object Object]'

export const normalizeJsonValue = (value: unknown): JsonValue | undefined => {
  if (value === undefined) return undefined
  if (value === null) return null
  if (typeof value === 'string' || typeof value === 'boolean') return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
  if (Array.isArray(value)) {
    return value.map((entry) => {
      const normalized = normalizeJsonValue(entry)
      return normalized === undefined ? null : normalized
    })
  }
  if (!isPlainObject(value)) return undefined

  const normalized: JsonObject = {}
  Object.entries(value).forEach(([key, entry]) => {
    const next = normalizeJsonValue(entry)
    if (next === undefined) return
    normalized[key] = next
  })
  return normalized
}

export const normalizeJsonObject = (value?: Record<string, unknown> | null): JsonObject => {
  if (!value || !isPlainObject(value)) return {}
  return (normalizeJsonValue(value) as JsonObject | undefined) ?? {}
}

export const cloneJsonValue = (value: JsonValue): JsonValue => {
  if (Array.isArray(value)) return value.map((entry) => cloneJsonValue(entry))
  if (value && typeof value === 'object') {
    const cloned: JsonObject = {}
    Object.entries(value).forEach(([key, entry]) => {
      cloned[key] = cloneJsonValue(entry)
    })
    return cloned
  }
  return value
}

export const cloneJsonObject = (value?: JsonObject | null): JsonObject => {
  if (!value) return {}
  const cloned: JsonObject = {}
  Object.entries(value).forEach(([key, entry]) => {
    cloned[key] = cloneJsonValue(entry)
  })
  return cloned
}

export const isJsonValueEqual = (left: JsonValue | undefined, right: JsonValue | undefined): boolean => {
  if (left === right) return true
  if (left === null || right === null) return left === right
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false
    if (left.length !== right.length) return false
    return left.every((entry, index) => isJsonValueEqual(entry, right[index]))
  }
  if (left && typeof left === 'object' && right && typeof right === 'object') {
    const leftKeys = Object.keys(left)
    const rightKeys = Object.keys(right)
    if (leftKeys.length !== rightKeys.length) return false
    return leftKeys.every(
      (key) => Object.prototype.hasOwnProperty.call(right, key) && isJsonValueEqual(left[key], right[key])
    )
  }
  return false
}

export const visitJsonStrings = (value: unknown, visitor: (value: string) => void) => {
  if (typeof value === 'string') {
    visitor(value)
    return
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => visitJsonStrings(entry, visitor))
    return
  }
  if (value && typeof value === 'object') {
    Object.values(value as Record<string, unknown>).forEach((entry) => visitJsonStrings(entry, visitor))
  }
}
